// src/pages/platform-admin/Universities.js

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Chip,
    Grid,
    Slide,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    useTheme,
    InputAdornment,
    Menu,
    Tooltip,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    Switch,
    FormControlLabel,
    FormGroup,
    MenuItem,
    Divider,
    FormControl,
    InputLabel,
    Select,
    useMediaQuery,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    ListSubheader
} from '@mui/material';
import {
    Delete,
    Search,
    Add as AddIcon,
    Close as CloseIcon,
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Edit as EditIcon,
    Visibility as ViewIcon,
    School,
    CheckCircle,
    Cancel,
    GetApp,
    PictureAsPdf,
    TableChart,
    Print,
    Business,
    CalendarToday,
    Update,
    Domain,
    LocationCity
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIUniversityList,
    APIUniversityNames,
    APIUniversityStore,
    APIUniversityUpdate,
    APIUniversityDelete,
    APIUniversityShow,
} from '../../apiServices/APIs';
import { 
    formatCreatedAtDate, 
    recordsCount, 
    recordsPerPage, 
    getUserDetails 
} from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import ErrorState from '../../components/ErrorState';
import LoadingState from '../../components/LoadingState';
import PlatformAdminBar from '../../components/PlatformAdminBar';
import authService from '../../apiServices/authService';
import { FullWidthContainer } from '../../components/FullWidthContainer';

// Transition component for dialogs
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const Universities = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const showToast = useCustomToast();
    const [updateId, setUpdateId] = useState('');
    const [postStatus, setPostStatus] = useState('ADD');
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [pageState, setPageState] = useState({
        pageData: [],
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    
    const currentUserId = getUserDetails("userNameId");

    // Form data
    const [formData, setFormData] = useState({
        name: '',
        is_active: true,
        schools: [] // Add schools array to form data
    });
    
    // School input state
    const [schoolInput, setSchoolInput] = useState('');
    const [editingSchoolIndex, setEditingSchoolIndex] = useState(null);
    
    const [filters, setFilters] = useState({
        is_active: '',
        search: ''
    });
    
    const [errors, setErrors] = useState({
        name: false,
        schools: false
    });
    
    // State for dialogs
    const [filterOptions, setFilterOptions] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    
    // Use ref to track if component is mounted
    const isMounted = useRef(true);

    // Handle logout
    const handleLogout = async () => {
        try {
            await authService.logout();
        } catch (error) {
            console.error('Logout error:', error);
        }
    };
    
    useEffect(() => {
        isMounted.current = true;
        apiService.setToastFunction(showToast);
        
        return () => {
            isMounted.current = false;
        };
    }, [showToast]);
    
    useEffect(() => {
        fetchData();
    }, []);
    
    // Fetch universities
    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        try {
            const result = await apiService.list(APIUniversityList);
            
            if (result.success && isMounted.current) {
                const universities = Array.isArray(result.data) ? result.data : [];
                setPageState(prev => ({
                    ...prev,
                    pageData: universities
                }));
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError("Failed to load data");
            console.error("Fetch error:", error);
        } finally {
            if (isMounted.current) setLoading(false);
        }
    }, []);
    
    // Filter based on search term and filters
    const filteredData = useMemo(() => {
        return pageState.pageData.filter(record => {
            const matchesSearch = searchTerm === '' ||
                (record.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                (record.university_code?.toLowerCase() || '').includes(searchTerm.toLowerCase());
            
            const matchesStatus = filters.is_active === '' || 
                (filters.is_active === 'active' && record.is_active) ||
                (filters.is_active === 'inactive' && !record.is_active);
            
            return matchesSearch && matchesStatus;
        });
    }, [pageState.pageData, searchTerm, filters.is_active]);
    
    // Pagination handlers
    const handleChangePage = useCallback((event, newPage) => setPage(newPage), []);
    const handleChangeRowsPerPage = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);
    
    // Dialog handlers
    const handlefilterOptions = useCallback(() => setFilterOptions(true), []);
    const handleCloseOptions = useCallback(() => setFilterOptions(false), []);
    
    const handleOpenAdd = useCallback(() => {
        setPostStatus("ADD");
        setFormData({
            name: '',
            is_active: true,
            schools: []
        });
        setSchoolInput('');
        setEditingSchoolIndex(null);
        setErrors({ name: false, schools: false });
        setOpenAdd(true);
    }, []);
    
    const handleCloseAdd = useCallback(() => setOpenAdd(false), []);
    
    const handleOpenView = useCallback((record) => {
        setSelectedRecord(record);
        setOpenView(true);
    }, []);
    
    const handleCloseView = useCallback(() => setOpenView(false), []);
    
    const handleOpenEdit = useCallback((record) => {
        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("UPDATE");
        
        // Parse schools data if it exists
        let schools = [];
        if (record.schools) {
            try {
                schools = typeof record.schools === 'string' ? JSON.parse(record.schools) : record.schools;
            } catch (e) {
                schools = [];
            }
        }
        
        setFormData({
            name: record.name || '',
            is_active: record.is_active === true || record.is_active === 1 || record.is_active === '1' || record.is_active === 'true',
            schools: schools
        });
        setSchoolInput('');
        setEditingSchoolIndex(null);
        setOpenAdd(true);
    }, []);

    const handleOpenDelete = useCallback((record) => {
        setSelectedRecord(record);
        setUpdateId(record.id);
        setPostStatus("DELETE");
        setOpenDelete(true);
    }, []);
    
    const handleCloseDelete = useCallback(() => setOpenDelete(false), []);
    
    // School management functions
    const handleAddSchool = useCallback(() => {
        const trimmedSchool = schoolInput.trim();
        if (!trimmedSchool) {
            showToast("Validation Error", "School name cannot be empty", "warning");
            return;
        }

        // Check for duplicate school names
        const duplicate = formData.schools.some(school => 
            school.name.toLowerCase() === trimmedSchool.toLowerCase()
        );
        
        if (duplicate) {
            showToast("Validation Error", "This school already exists in the list", "warning");
            return;
        }

        if (editingSchoolIndex !== null) {
            // Update existing school
            const updatedSchools = [...formData.schools];
            updatedSchools[editingSchoolIndex] = {
                ...updatedSchools[editingSchoolIndex],
                name: trimmedSchool
            };
            setFormData(prev => ({ ...prev, schools: updatedSchools }));
            setEditingSchoolIndex(null);
            showToast("Success", "School updated successfully", "success");
        } else {
            // Add new school
            const newSchool = {
                id: Date.now(), // Temporary ID for frontend
                name: trimmedSchool
            };
            setFormData(prev => ({ 
                ...prev, 
                schools: [...prev.schools, newSchool] 
            }));
            showToast("Success", "School added successfully", "success");
        }
        
        setSchoolInput('');
        setErrors(prev => ({ ...prev, schools: false }));
    }, [schoolInput, formData.schools, editingSchoolIndex, showToast]);

    const handleEditSchool = useCallback((index) => {
        setSchoolInput(formData.schools[index].name);
        setEditingSchoolIndex(index);
        // Focus the input field
        const inputElement = document.getElementById('school-input');
        if (inputElement) inputElement.focus();
    }, [formData.schools]);

    const handleRemoveSchool = useCallback((index) => {
        const updatedSchools = formData.schools.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, schools: updatedSchools }));
        
        if (editingSchoolIndex === index) {
            setEditingSchoolIndex(null);
            setSchoolInput('');
        }
        showToast("Success", "School removed successfully", "success");
    }, [formData.schools, editingSchoolIndex, showToast]);

    const handleKeyPress = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddSchool();
        }
    }, [handleAddSchool]);
    
    // Form handlers
    const handleInputChange = useCallback((e) => {
        const { name, value, checked, type } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        
        setErrors(prev => ({ ...prev, [name]: false }));
        setFormData(prev => ({ ...prev, [name]: newValue }));
    }, []);
    
    // Filter handlers
    const handleFilterChange = useCallback((e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    }, []);
    
    const clearFilters = useCallback(() => {
        setFilters({
            is_active: '',
            search: ''
        });
        setSearchTerm('');
        handleCloseOptions();
    }, [handleCloseOptions]);
    
    const validateForm = useCallback(() => {
        const errorsList = [];
        if (!formData.name.trim()) errorsList.push('University name is required');
        if (formData.schools.length === 0) errorsList.push('At least one school is required');
        
        if (errorsList.length > 0) {
            showToast("Validation Error", errorsList.join('\n'), "warning");
            setErrors(prev => ({
                ...prev,
                name: !formData.name.trim(),
                schools: formData.schools.length === 0
            }));
            return false;
        }
        return true;
    }, [formData.name, formData.schools, showToast]);
    
    const handleDialogClose = useCallback(() => {
        setOpenAdd(false);
        setOpenView(false);
        setOpenDelete(false);
        setFilterOptions(false);
        setSchoolInput('');
        setEditingSchoolIndex(null);
    }, []);
    
    const handleFormDataReset = useCallback(() => {
        setFormData({
            name: '',
            is_active: true,
            schools: []
        });
        setSchoolInput('');
        setEditingSchoolIndex(null);
    }, []);
    
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        
        if (postStatus !== "DELETE" && !validateForm()) return;
        
        const formDataToSend = new FormData();
        formDataToSend.append('user_id', getUserDetails("Id"));
        
        if (postStatus !== "DELETE") {
            formDataToSend.append('name', formData.name);
            formDataToSend.append('is_active', formData.is_active ? '1' : '0');
            
            // Convert schools to JSON string
            const schoolsJson = JSON.stringify(formData.schools.map(school => ({
                name: school.name
            })));
            formDataToSend.append('schools', schoolsJson);
        }
        
        if (postStatus === "UPDATE" || postStatus === "DELETE") {
            formDataToSend.append('updateId', updateId);
        }
        
        try {
            let result;
            if (postStatus === "ADD") {
                result = await apiService.create(APIUniversityStore, formDataToSend);
            } else if (postStatus === "UPDATE") {
                result = await apiService.update(APIUniversityUpdate, formDataToSend);
            } else {
                result = await apiService.delete(APIUniversityDelete, formDataToSend);
            }
            
            if (result && result.success && isMounted.current) {
                showToast("Success", result.message, "success");
                handleFormDataReset();
                handleDialogClose();
                fetchData();
            } else if (result && !result.success) {
                showToast("Error", result.message || "Operation failed", "error");
            }
        } catch (error) {
            console.error("API Error:", error);
            showToast("Error", error.response?.data?.message || "Operation failed", "error");
        }
    }, [postStatus, validateForm, formData, updateId, showToast, handleFormDataReset, handleDialogClose, fetchData]);

    if (loading) {
        return <LoadingState message="Loading Universities..." variant="research" size="large" fullPage />;
    }

    if (error) {
        return <ErrorState error={error} onRetry={fetchData} title="Unable to Load Universities" variant="research" fullPage retryText="Retry Loading" />;
    }

    return (
        <>
            <FullWidthContainer >
                <Box sx={{backgroundColor: 'white',borderRadius: 3 }}>
                    <PlatformAdminBar  notificationCount={65} />
                <Paper elevation={2} sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    mb: { xs: 2, sm: 3 }, 
                    borderRadius: 3,
                    backgroundColor: 'white',
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2, 
                        flexWrap: 'wrap',
                        flexDirection: { xs: 'column', sm: 'row' }
                    }}>
                        {/* Title */}
                        <Typography sx={{
                            fontWeight: 'bold',
                            color: theme.palette.primary.main,
                            flexShrink: 0,
                            fontSize: { xs: '1rem', sm: '1.25rem' }
                        }}>
                            Universities ({recordsCount(pageState.pageData.length)} Records)
                        </Typography>

                        {/* Buttons */}
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 1, 
                            flexShrink: 0,
                            width: { xs: '100%', sm: 'auto' },
                            justifyContent: { xs: 'center', sm: 'flex-start' }
                        }}>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleOpenAdd}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    flex: { xs: 1, sm: 'none' }
                                }}
                                size={isMobile ? "small" : "medium"}
                            >
                                Add University
                            </Button>

                            <Button
                                variant="contained"
                                color="secondary"
                                startIcon={<FilterIcon />}
                                onClick={handlefilterOptions}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    flex: { xs: 1, sm: 'none' }
                                }}
                                size={isMobile ? "small" : "medium"}
                            >
                                View Options
                            </Button>
                            
                            <Button
                                variant="outlined"
                                color="info"
                                startIcon={<RefreshIcon />}
                                onClick={fetchData}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    flex: { xs: 1, sm: 'none' }
                                }}
                                size={isMobile ? "small" : "medium"}
                            >
                                Refresh
                            </Button>
                        </Box>

                        {/* Search Bar */}
                        <Paper sx={{
                            width: { xs: '100%', sm: '300px' },
                            p: '2px 8px',
                            display: 'flex',
                            alignItems: 'center',
                            marginLeft: { xs: 0, sm: 'auto' },
                            borderRadius: '8px',
                            backgroundColor: theme.palette.grey[50],
                        }}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Search universities..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: <Search sx={{ mr: 1, color: theme.palette.grey[600] }} />,
                                    sx: {
                                        '& fieldset': { border: 'none' },
                                        fontSize: '0.875rem',
                                    }
                                }}
                                size="small"
                            />
                        </Paper>
                    </Box>

                    {/* Active Filters */}
                    {filters.is_active && (
                        <Box sx={{ 
                            display: 'flex', 
                            gap: 1, 
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            mt: 2,
                            pt: 2,
                            borderTop: '1px solid',
                            borderColor: 'divider'
                        }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mr: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                Active filters:
                            </Typography>
                            {filters.is_active && (
                                <Chip
                                    label={`Status: ${filters.is_active === 'active' ? 'Active' : 'Inactive'}`}
                                    onDelete={() => setFilters(prev => ({ ...prev, is_active: '' }))}
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                />
                            )}
                            <Button
                                size="small"
                                onClick={clearFilters}
                                startIcon={<CloseIcon />}
                                sx={{ ml: 1 }}
                            >
                                Clear All
                            </Button>
                        </Box>
                    )}
                </Paper>
                </Box>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <Paper elevation={2} sx={{ 
                        borderRadius: 3, 
                        overflow: 'auto',
                        backgroundColor: 'white',
                    }}>
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table sx={{ minWidth: { xs: 600, sm: 700, md: 800 } }}>
                                <TableHead>
                                    <TableRow sx={{
                                        backgroundColor: theme.palette.primary.main,
                                        '& th': {
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' }
                                        }
                                    }}>
                                        <TableCell>#</TableCell>
                                        <TableCell>Code</TableCell>
                                        <TableCell>University Name</TableCell>
                                        <TableCell>Schools Count</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Created Date</TableCell>
                                        <TableCell align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((record, index) => {
                                        // Parse schools to get count
                                        let schoolsCount = 0;
                                        if (record.schools) {
                                            try {
                                                const schools = typeof record.schools === 'string' ? JSON.parse(record.schools) : record.schools;
                                                schoolsCount = Array.isArray(schools) ? schools.length : 0;
                                            } catch (e) {
                                                schoolsCount = 0;
                                            }
                                        }
                                        
                                        return (
                                        <TableRow
                                            key={record.id}
                                            hover
                                            sx={{
                                                '&:nth-of-type(odd)': {
                                                    backgroundColor: theme.palette.action.hover
                                                }
                                            }}
                                        >
                                            <TableCell>
                                                <Chip
                                                    label={page * rowsPerPage + index + 1}
                                                    color="primary"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={<Business />}
                                                    label={record.university_code}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <School color="primary" fontSize="small" />
                                                    <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                        {record.name}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={<LocationCity />}
                                                    label={schoolsCount}
                                                    color="info"
                                                    size="small"
                                                    sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    icon={record.is_active ? <CheckCircle /> : <Cancel />}
                                                    label={record.status_text}
                                                    color={record.is_active ? "success" : "error"}
                                                    size="small"
                                                    sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                {formatCreatedAtDate(record.created_at)}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                                                    <Tooltip title="View">
                                                        <IconButton onClick={() => handleOpenView(record)} color="primary" size="small">
                                                            <ViewIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Edit">
                                                        <IconButton onClick={() => handleOpenEdit(record)} color="secondary" size="small">
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton onClick={() => handleOpenDelete(record)} color="error" size="small">
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )})}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50, 100]}
                            component="div"
                            count={filteredData.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{ mt: 2 }}
                        />

                        {filteredData.length === 0 && (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                py: { xs: 6, sm: 8 },
                                color: 'text.secondary'
                            }}>
                                <School sx={{ fontSize: { xs: 48, sm: 60 }, mb: 2 }} />
                                <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    No universities found
                                </Typography>
                                <Typography variant="body2">Try adjusting your search query</Typography>
                            </Box>
                        )}
                    </Paper>
                </motion.div>

                {/* Filter Dialog */}
                <Dialog
                    open={filterOptions}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="sm"
                    disableEscapeKeyDown
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            handleCloseOptions();
                        }
                    }}
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ textAlign: 'center', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Filter Options
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: { xs: 1, sm: 2 } }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<RefreshIcon />}
                                onClick={clearFilters}
                                sx={{ textTransform: 'none' }}
                                size={isMobile ? "small" : "medium"}
                            >
                                Show All Records
                            </Button>

                            <Divider>OR</Divider>

                            <Typography variant="subtitle1" sx={{ textAlign: 'center', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                Filter by Status
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            name="is_active"
                                            value={filters.is_active}
                                            onChange={handleFilterChange}
                                            label="Status"
                                        >
                                            <MenuItem value="">All Status</MenuItem>
                                            <MenuItem value="active">Active</MenuItem>
                                            <MenuItem value="inactive">Inactive</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button
                            color="error"
                            onClick={handleCloseOptions}
                            variant="contained"
                            size={isMobile ? "small" : "medium"}
                        >
                            Close
                        </Button>
                        <Button
                            onClick={handleCloseOptions}
                            color="primary"
                            variant="contained"
                            size={isMobile ? "small" : "medium"}
                        >
                            Apply Filters
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Add/Edit Dialog */}
                <Dialog
                    open={openAdd}
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            handleCloseAdd();
                        }
                    }}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="sm"
                    scroll="paper"
                    disableEscapeKeyDown
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ textAlign: 'center', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        <b>{postStatus === "ADD" ? "Add New University" : "Edit University"}</b>
                    </DialogTitle>
                    <DialogContent>
                        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="University Name *"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        error={errors.name}
                                        helperText={errors.name ? "University name is required" : ""}
                                        size={isMobile ? "small" : "medium"}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <School />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                
                                <Grid item xs={12}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="is_active"
                                                checked={formData.is_active}
                                                onChange={handleInputChange}
                                                color="success"
                                            />
                                        }
                                        label={formData.is_active ? "Active" : "Inactive"}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Divider sx={{ my: 1 }} />
                                    <Typography variant="subtitle1" sx={{ 
                                        fontWeight: 'bold', 
                                        color: theme.palette.primary.main,
                                        mb: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }}>
                                        <LocationCity /> Schools ({formData.schools.length})
                                    </Typography>
                                    
                                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                        <TextField
                                            id="school-input"
                                            fullWidth
                                            label="Enter school name"
                                            value={schoolInput}
                                            onChange={(e) => setSchoolInput(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            error={errors.schools}
                                            helperText={errors.schools ? "At least one school is required" : ""}
                                            size={isMobile ? "small" : "medium"}
                                            placeholder="e.g., School of Engineering"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <LocationCity />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={handleAddSchool}
                                            sx={{ 
                                                minWidth: '100px',
                                                textTransform: 'none'
                                            }}
                                            size={isMobile ? "small" : "medium"}
                                        >
                                            {editingSchoolIndex !== null ? 'Update' : 'Add'}
                                        </Button>
                                    </Box>

                                    {/* Schools List */}
                                    {formData.schools.length > 0 && (
                                        <Paper 
                                            variant="outlined" 
                                            sx={{ 
                                                maxHeight: '200px', 
                                                overflow: 'auto',
                                                bgcolor: theme.palette.grey[50]
                                            }}
                                        >
                                            <List dense>
                                                {formData.schools.map((school, index) => (
                                                    <ListItem
                                                        key={school.id || index}
                                                        divider
                                                        sx={{
                                                            '&:hover': {
                                                                bgcolor: theme.palette.action.hover
                                                            }
                                                        }}
                                                        secondaryAction={
                                                            <Box>
                                                                <IconButton 
                                                                    edge="end" 
                                                                    onClick={() => handleEditSchool(index)}
                                                                    size="small"
                                                                    color="primary"
                                                                    sx={{ mr: 1 }}
                                                                >
                                                                    <EditIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton 
                                                                    edge="end" 
                                                                    onClick={() => handleRemoveSchool(index)}
                                                                    size="small"
                                                                    color="error"
                                                                >
                                                                    <Delete fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                        }
                                                    >
                                                        <ListItemAvatar>
                                                            <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                                                                <Domain />
                                                            </Avatar>
                                                        </ListItemAvatar>
                                                        <ListItemText 
                                                            primary={school.name}
                                                            primaryTypographyProps={{
                                                                fontSize: '0.875rem'
                                                            }}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Paper>
                                    )}
                                </Grid>
                            </Grid>

                            <DialogActions sx={{ mt: 3, px: 0 }}>
                                <Button
                                    color="error"
                                    onClick={handleCloseAdd}
                                    variant="contained"
                                    size={isMobile ? "small" : "medium"}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    variant="contained"
                                    size={isMobile ? "small" : "medium"}
                                >
                                    {postStatus === "ADD" ? "Add University" : "Update University"}
                                </Button>
                            </DialogActions>
                        </Box>
                    </DialogContent>
                </Dialog>

                {/* View Dialog */}
                <Dialog
                    open={openView}
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            handleCloseView();
                        }
                    }}
                    TransitionComponent={Transition}
                    fullWidth
                    maxWidth="sm"
                    disableEscapeKeyDown
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ textAlign: 'center', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        <b>University Details</b>
                    </DialogTitle>
                    <DialogContent>
                        {selectedRecord && (
                            <Box sx={{ p: { xs: 1, sm: 2 } }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Box sx={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'center', 
                                            mb: 2,
                                            flexDirection: { xs: 'column', sm: 'row' },
                                            gap: { xs: 1, sm: 0 }
                                        }}>
                                            <Typography variant="h6" color="primary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                                University #{selectedRecord.id}
                                            </Typography>
                                            <Chip
                                                icon={selectedRecord.is_active ? <CheckCircle /> : <Cancel />}
                                                label={selectedRecord.status_text}
                                                color={selectedRecord.is_active ? "success" : "error"}
                                                size="small"
                                            />
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" sx={{ 
                                            fontWeight: 'bold', 
                                            color: theme.palette.primary.main, 
                                            mb: 1,
                                            fontSize: { xs: '0.9rem', sm: '1rem' }
                                        }}>
                                            University Information
                                        </Typography>
                                        <Typography variant="subtitle2" sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 1, 
                                            mb: 1,
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                        }}>
                                            <Business fontSize="small" color="primary" />
                                            <b>Code:</b> {selectedRecord.university_code}
                                        </Typography>
                                        <Typography variant="subtitle2" sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 1, 
                                            mb: 1,
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                        }}>
                                            <School fontSize="small" color="secondary" />
                                            <b>Name:</b> {selectedRecord.name}
                                        </Typography>
                                    </Grid>

                                    {/* Schools Section */}
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" sx={{ 
                                            fontWeight: 'bold', 
                                            color: theme.palette.primary.main, 
                                            mb: 2, 
                                            mt: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            fontSize: { xs: '0.9rem', sm: '1rem' }
                                        }}>
                                            <LocationCity /> Schools
                                        </Typography>
                                        
                                        {(() => {
                                            let schools = [];
                                            if (selectedRecord.schools) {
                                                try {
                                                    schools = typeof selectedRecord.schools === 'string' 
                                                        ? JSON.parse(selectedRecord.schools) 
                                                        : selectedRecord.schools;
                                                    if (!Array.isArray(schools)) schools = [];
                                                } catch (e) {
                                                    schools = [];
                                                }
                                            }
                                            
                                            if (schools.length === 0) {
                                                return (
                                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                        No schools associated with this university
                                                    </Typography>
                                                );
                                            }
                                            
                                            return (
                                                <Paper 
                                                    variant="outlined" 
                                                    sx={{ 
                                                        maxHeight: '200px', 
                                                        overflow: 'auto',
                                                        bgcolor: theme.palette.grey[50]
                                                    }}
                                                >
                                                    <List dense>
                                                        {schools.map((school, index) => (
                                                            <ListItem key={school.id || index} divider>
                                                                <ListItemAvatar>
                                                                    <Avatar sx={{ 
                                                                        bgcolor: theme.palette.primary.main,
                                                                        width: 24,
                                                                        height: 24
                                                                    }}>
                                                                        <Domain sx={{ fontSize: 14 }} />
                                                                    </Avatar>
                                                                </ListItemAvatar>
                                                                <ListItemText 
                                                                    primary={school.name}
                                                                    primaryTypographyProps={{
                                                                        fontSize: '0.875rem'
                                                                    }}
                                                                />
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </Paper>
                                            );
                                        })()}
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Typography variant="subtitle1" sx={{ 
                                            fontWeight: 'bold', 
                                            color: theme.palette.primary.main, 
                                            mb: 1, 
                                            mt: 2,
                                            fontSize: { xs: '0.9rem', sm: '1rem' }
                                        }}>
                                            System Information
                                        </Typography>
                                        <Typography variant="subtitle2" sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 1, 
                                            mb: 1,
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                        }}>
                                            <CalendarToday fontSize="small" color="info" />
                                            <b>Created Date:</b> {formatCreatedAtDate(selectedRecord.created_at)}
                                        </Typography>
                                        <Typography variant="subtitle2" sx={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: 1, 
                                            mb: 1,
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                        }}>
                                            <Update fontSize="small" color="info" />
                                            <b>Last Updated:</b> {formatCreatedAtDate(selectedRecord.updated_at)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button color="error" onClick={handleCloseView} variant="contained" size={isMobile ? "small" : "medium"}>
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog
                    open={openDelete}
                    onClose={(event, reason) => {
                        if (reason !== 'backdropClick') {
                            handleCloseDelete();
                        }
                    }}
                    TransitionComponent={Transition}
                    disableEscapeKeyDown
                    PaperProps={{
                        sx: {
                            m: { xs: 1, sm: 2 },
                            width: { xs: 'calc(100% - 16px)', sm: 'auto' }
                        }
                    }}
                >
                    <DialogTitle sx={{ textAlign: 'center', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        Delete University
                    </DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" sx={{ textAlign: 'center', mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                            Are you sure you want to delete this university?
                        </Typography>
                        {selectedRecord && (
                            <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                {selectedRecord.name}
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                    Code: {selectedRecord.university_code}
                                </Typography>
                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}>
                                    <Chip
                                        icon={<School />}
                                        label={selectedRecord.name}
                                        color="primary"
                                        variant="outlined"
                                        size="small"
                                    />
                                    <Chip
                                        icon={selectedRecord.is_active ? <CheckCircle /> : <Cancel />}
                                        label={selectedRecord.status_text}
                                        color={selectedRecord.is_active ? "success" : "error"}
                                        size="small"
                                    />
                                </Box>
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
                        <Button
                            color="error"
                            onClick={handleCloseDelete}
                            variant="contained"
                            size={isMobile ? "small" : "medium"}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            color="primary"
                            variant="contained"
                            size={isMobile ? "small" : "medium"}
                        >
                            Confirm Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </FullWidthContainer>
        </>
    );
};

export default Universities;